import React from 'react';
import { CircularProgress } from '@material-ui/core';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventIcon from '@mui/icons-material/Event';
import AuthUtil from '../../redux/AuthUtil';
import { COMPANY as COMPANY_ENDPOINTS } from '../global/Server/endpoints';
import PricingPlans from '../landing/PricingPlans';
import {
  PLANS,
  PUBLIC_PLAN_ORDER,
  getAvailableAddOns,
} from '../settings/ManageCompany/wizardConfig';
import {
  ensurePlanCatalog,
  subscribeToPlanCatalog,
} from '../settings/ManageCompany/planCatalogRuntime';
import '../landing/LandingPage.css';
import scrollAppToTop from '../Utils/scrollToTop';

/**
 * Profile > Subscription > Manage subscription.
 *
 * Its own screen, showing exactly what the landing page sells — the same
 * PricingPlans cards — with this company's controls on each card in place
 * of "Get Started". The marketing blocks below the cards (the read-only
 * add-on rate card and Compare Plans) are off: a customer choosing between
 * plans they hold today doesn't need the shopping grid, and add-ons are
 * picked here rather than listed.
 *
 * Confirming a switch opens the same add-on picker the sign-up wizard uses
 * (OnboardingWizard step 1): the add-ons the target plan doesn't already
 * bundle, all starting unticked, exactly as a new customer sees them. They
 * ride along with the switch, because a tier change is exactly when they
 * change meaning — moving up absorbs an add-on into the plan, moving down
 * releases it — and billing them apart would charge for a module the
 * paid-for cycle never included. Nothing carries over on its own, so while
 * an add-on the company pays for today is left unticked the picker says
 * so — the price is confirmed on what's ticked, not on what they hold.
 *
 * A switch is SCHEDULED for the next billing date (CompanyService
 * .schedulePlanChange): features and price move together then, so nobody
 * loses what they already paid for, and it can be cancelled until it
 * applies. Only the company's owner and NuVe System owners may switch
 * (`choices.canSwitch`); everyone else sees the plans read-only.
 */

const C = {
  navy: '#0f1847',
  navyCard: '#1a2666',
  gold: '#D4AF37',
  ink: '#0A1640',
  muted: '#5B6781',
  line: '#E5EAF4',
  amber: '#b45309',
  amberBg: '#fef3c7',
  red: '#dc2626',
  redBg: '#fee2e2',
};

// 'yyyy-MM-dd' → "Oct 15, 2026" without a timezone shift.
function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return String(iso);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const money = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return '';
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
};

// The card's button spacing, tightened when two stack.
const stacked = { margin: '6px 0' };

// The add-on picker, kept visually identical to OnboardingWizard step 1 —
// same row, same checkbox, same selected blue — so buying an add-on looks
// the same whether you're signing up or already a customer.
const addOnLabel = {
  fontSize: 12,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: '#9aa6bd',
  marginBottom: 8,
};

const addonRow = (on) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '9px 12px',
  borderRadius: 11,
  marginBottom: 8,
  cursor: 'pointer',
  fontSize: 13.5,
  background: on ? '#EEF3FF' : '#fff',
  border: `1.5px solid ${on ? '#1E3FD0' : C.line}`,
});

const dropNote = {
  fontSize: 12,
  lineHeight: 1.4,
  color: C.amber,
  background: C.amberBg,
  border: '1px solid #fcd34d',
  borderRadius: 10,
  padding: '7px 10px',
  marginTop: 2,
};

const checkbox = (on) => ({
  width: 20,
  height: 20,
  borderRadius: 6,
  display: 'grid',
  placeItems: 'center',
  fontSize: 13,
  color: '#fff',
  flexShrink: 0,
  background: on ? '#1E3FD0' : 'transparent',
  border: `2px solid ${on ? '#1E3FD0' : '#c5cdda'}`,
});

class ManageSubscriptionScreen extends React.Component {
  state = {
    confirming: null, // plan id awaiting confirmation
    addOnIds: [], // add-ons ticked for the plan being confirmed
    saving: false,
    error: '',
    choices: null, // fresh copy from the last change, until the parent refetches
  };

  componentDidMount() {
    this.mounted = true;
    // The cards read the live rate card, which may not have loaded yet in
    // the app (the landing page loads it for itself).
    this.unsubscribe = subscribeToPlanCatalog(() => {
      if (this.mounted) this.forceUpdate();
    });
    ensurePlanCatalog();
    try {
      scrollAppToTop();
    } catch (e) {
      /* scrolling is cosmetic */
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.unsubscribe) this.unsubscribe();
  }

  choices() {
    return this.state.choices || this.props.choices || { plans: [] };
  }

  choiceFor(pid) {
    return (this.choices().plans || []).find((p) => p.id === pid) || null;
  }

  // ---- add-ons ------------------------------------------------------------

  // What a plan can still sell on top of itself — the wizard's own rule, so
  // the two screens never offer different modules for the same plan.
  addOnsFor(pid) {
    return getAvailableAddOns(pid);
  }

  // Nothing is ticked by default — an add-on is a purchase, and the
  // customer picks it deliberately here the same way they do at sign-up.
  // Add-ons they already pay for are therefore NOT carried over on their
  // own; `heldAddOnsFor` surfaces them in the picker so dropping one is a
  // choice rather than a surprise on the next invoice.
  startConfirm = (pid) => {
    this.setState({ confirming: pid, addOnIds: [], error: '' });
  };

  // Add-ons this company pays for today that the target plan could still
  // sell it (`choice.heldAddOns` — NOT `choice.addOns`, which is what that
  // row's quoted price includes and is empty for every plan but a
  // scheduled one). The company's current list is the fallback for an
  // older payload.
  heldAddOnsFor(pid) {
    const choice = this.choiceFor(pid);
    const available = new Set(this.addOnsFor(pid).map((a) => a.id));
    const held = (choice && choice.heldAddOns) || this.choices().addOns || [];
    return held.filter((id) => available.has(id));
  }

  toggleAddOn = (id) =>
    this.setState((s) => ({
      addOnIds: s.addOnIds.includes(id)
        ? s.addOnIds.filter((x) => x !== id)
        : [...s.addOnIds, id],
    }));

  // The monthly this company would pay on `plan` with the ticked add-ons.
  // `list` is the rate card; `effective` applies this plan's discount the
  // way CompanyService.effectiveMonthlyRate does — discount the whole
  // cycle, round to whole dollars, then divide back per month.
  //
  // It has to mirror that arithmetic step for step, not scale by the
  // effective/list ratio the server happened to quote: the rounding is
  // applied to the cycle, so the ratio is only exact for the add-on set
  // the server priced. Ticking Customer Mobile onto a 10% Regular made
  // 475 x 0.9 = 427.5 round UP to 428, while the server takes
  // 475 - round(47.5) = 427 — the screen quoted a dollar more than the
  // invoice would charge.
  previewMonthly(pid, plan) {
    const addOnTotal = this.addOnsFor(pid)
      .filter((a) => this.state.addOnIds.includes(a.id))
      .reduce((sum, a) => sum + (Number(a.price) || 0), 0);
    const list = (Number(plan.price) || 0) + addOnTotal;
    const choice = this.choiceFor(pid) || {};
    if (choice.discountPct == null) {
      // Older payload without the breakdown: fall back to the ratio
      // between the two figures it did send.
      const quotedList = Number(choice.listMonthly);
      const quotedEffective = Number(choice.effectiveMonthly);
      const ratio =
        quotedList > 0 && quotedEffective > 0
          ? quotedEffective / quotedList
          : 1;
      return { list, effective: Math.round(list * ratio) };
    }
    const months = Math.max(Number(this.choices().billingMonths) || 1, 1);
    const pct = (Number(choice.discountPct) || 0) / 100;
    const gross = list * months;
    const discount = Math.round(gross * pct);
    return { list, effective: Math.round((gross - discount) / months) };
  }

  send = async (endpoint, body) => {
    this.setState({ saving: true, error: '' });
    try {
      const res = await AuthUtil.postRequest(endpoint, body);
      const data = (res && res.data) || {};
      if (data.success === false) {
        this.setState({
          saving: false,
          error: data.message || 'The change could not be saved.',
        });
        return;
      }
      this.setState({
        saving: false,
        confirming: null,
        addOnIds: [],
        choices: data.planChoices || null,
      });
      if (this.props.onChanged) this.props.onChanged(data);
    } catch (e) {
      this.setState({ saving: false, error: 'The change could not be saved.' });
    }
  };

  switchTo = (pid) =>
    this.send(COMPANY_ENDPOINTS.SCHEDULE_PLAN_CHANGE, {
      companyId: this.props.companyId,
      planId: pid,
      // Sent even when empty — the server reads an absent list as "keep
      // the add-ons they hold", which is the opposite of clearing them.
      addOnIds: this.state.addOnIds,
    });

  cancelSwitch = () =>
    this.send(COMPANY_ENDPOINTS.CANCEL_PLAN_CHANGE, {
      companyId: this.props.companyId,
    });

  // ---- per-card hooks for PricingPlans ------------------------------------

  cardClassName = (pid) => {
    const c = this.choiceFor(pid);
    if (c && c.current) return 'nv-current';
    if (c && c.pending) return 'nv-scheduled';
    return '';
  };

  renderNameBadge = (pid) => {
    const c = this.choiceFor(pid);
    if (c && c.current) return <span className="nv-px-current">Current</span>;
    if (c && c.pending) {
      return <span className="nv-px-current nv-px-sched">Scheduled</span>;
    }
    return null;
  };

  // What THIS company would pay: its add-ons, discount group and billing
  // duration — the card above it keeps the landing page's list price.
  /**
   * Whether this card should show a discounted price, and the numbers
   * behind it. Null when the company pays the sticker price, or while the
   * card is being confirmed — the add-on ticks move the price then, and
   * the confirm block below already quotes it live, so a second
   * pre-selection number above would contradict it.
   */
  discountFor(pid, plan) {
    if (this.state.confirming === pid) return null;
    const c = this.choiceFor(pid);
    if (!c || c.effectiveMonthly == null) return null;
    const list = Number(plan.price);
    const pay = Number(c.effectiveMonthly);
    if (!(pay < list)) return null;
    return { choice: c, list, pay, saves: list - pay };
  }

  /**
   * The price a card leads with. With a discount the figure they actually
   * pay is the headline and the list price is struck through beside it —
   * the ordinary way a reduced price reads. Showing the list price big and
   * the real one in a small pill underneath left the reader to work out
   * which number they pay, and why it differed.
   */
  renderPrice = (pid, plan) => {
    const deal = this.discountFor(pid, plan);
    if (!deal) return null;
    return (
      <div className="nv-px-price nv-px-price--deal">
        <span className="nv-amt">{money(deal.pay)}</span>
        <span className="nv-per">/mo</span>
        <span className="nv-px-was">{money(deal.list)}</span>
        <span className="nv-px-save">Save {money(deal.saves)}/mo</span>
      </div>
    );
  };

  /**
   * The arithmetic under the price: the sticker, each discount that came
   * off it, and what is left. Both discounts are per plan — the
   * multi-location tiers start at 2 locations on Regular and Premium but
   * at 10 on Basic, and Basic gets no prepay saving at all — so two cards
   * can legitimately show different reasons for the same company.
   */
  renderPriceNote = (pid, plan) => {
    const deal = this.discountFor(pid, plan);
    if (!deal) return null;
    const { choice } = deal;
    const months = this.choices().billingMonths;
    const rows = [
      choice.locationDiscountPct > 0 && {
        key: 'loc',
        label: `Multi-location · ${choice.activeLocations} active`,
        value: `−${choice.locationDiscountPct}%`,
      },
      choice.prepayDiscountPct > 0 && {
        key: 'prepay',
        label: `${months}-month prepay`,
        value: `−${choice.prepayDiscountPct}%`,
      },
    ].filter(Boolean);
    return (
      <div className="nv-px-why">
        <div className="nv-px-why__head">
          <CheckCircleIcon style={{ fontSize: 14 }} />
          Why this price
        </div>
        <div className="nv-px-why__row">
          <span>{plan.name} list price</span>
          <b style={{ color: C.muted }}>{money(deal.list)}/mo</b>
        </div>
        {rows.map((r) => (
          <div className="nv-px-why__row" key={r.key}>
            <span>{r.label}</span>
            <b>{r.value}</b>
          </div>
        ))}
        <div className="nv-px-why__total">
          <span>You pay</span>
          <span>{money(deal.pay)}/mo</span>
        </div>
      </div>
    );
  };

  // The wizard's add-on rows (checkbox · name · +$x/mo), sized for a plan
  // card.
  renderAddOnPicker(pid) {
    const addOns = this.addOnsFor(pid);
    if (addOns.length === 0) return null;
    const held = this.heldAddOnsFor(pid);
    const dropping = held.filter((id) => !this.state.addOnIds.includes(id));
    return (
      <div style={{ textAlign: 'left', margin: '14px 0 12px' }}>
        <div style={addOnLabel}>Add-ons (optional)</div>
        {addOns.map((a) => {
          const on = this.state.addOnIds.includes(a.id);
          return (
            <div
              key={a.id}
              onClick={() => this.toggleAddOn(a.id)}
              style={addonRow(on)}
            >
              <span style={checkbox(on)}>{on ? '✓' : ''}</span>
              <span style={{ flex: 1, fontWeight: 600, color: C.ink }}>
                {a.name}
              </span>
              <span style={{ fontWeight: 700, color: '#1E3FD0' }}>
                +${a.price}/mo
              </span>
            </div>
          );
        })}
        {/* Nothing carries over on its own, so say so while something they
            pay for today is still unticked. */}
        {dropping.length > 0 && (
          <div style={dropNote}>
            Tick what you want to keep — add-ons you leave off end with this
            plan.
          </div>
        )}
      </div>
    );
  }

  renderAction = (pid, plan) => {
    const choices = this.choices();
    const c = this.choiceFor(pid);
    const { saving, confirming } = this.state;
    const tone = plan.badge ? 'nv-btn-primary' : 'nv-btn-blue';
    // Checked before `current`: the add-ons of the plan they're already on
    // are edited through the same confirm step.
    if (confirming === pid) {
      const staying = !!(c && c.current);
      const { list, effective } = this.previewMonthly(pid, plan);
      return (
        <div style={{ margin: '12px 0' }}>
          {this.renderAddOnPicker(pid)}
          <div style={{ fontSize: 13, color: C.muted, textAlign: 'center' }}>
            {staying ? 'Add-ons change' : `Switch to ${plan.name}`} on{' '}
            {fmtDate(choices.switchDate)}
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 800,
              color: C.ink,
              textAlign: 'center',
              marginTop: 2,
            }}
          >
            {money(effective)}/mo
            {effective !== list ? (
              <span style={{ fontWeight: 600, color: C.muted }}>
                {' '}
                ({money(list)} before your discount)
              </span>
            ) : null}
          </div>
          <button
            className={`nv-btn ${tone}`}
            style={stacked}
            disabled={saving}
            onClick={() => this.switchTo(pid)}
          >
            {saving ? (
              <CircularProgress size={14} style={{ color: 'inherit' }} />
            ) : null}
            {staying ? 'Confirm add-ons' : 'Confirm switch'}
          </button>
          <button
            className="nv-btn nv-btn-ghost-d"
            style={stacked}
            disabled={saving}
            onClick={() => this.setState({ confirming: null, addOnIds: [] })}
          >
            Back
          </button>
        </div>
      );
    }
    if (c && c.current) {
      // Add-ons are a live purchase on the current plan too, so the card
      // they're billed under is where they're changed.
      const canEditAddOns =
        choices.canSwitch && this.addOnsFor(pid).length > 0 && !choices.pending;
      return (
        <div style={{ margin: '12px 0' }}>
          <button
            className="nv-btn nv-btn-blue"
            disabled
            style={canEditAddOns ? stacked : undefined}
          >
            ✓ Current plan
          </button>
          {canEditAddOns && (
            <button
              className="nv-btn nv-btn-ghost-d"
              style={stacked}
              onClick={() => this.startConfirm(pid)}
            >
              Change add-ons
            </button>
          )}
        </div>
      );
    }
    if (c && c.pending) {
      return (
        <div style={{ margin: '12px 0' }}>
          <button className="nv-btn nv-btn-gold" disabled style={stacked}>
            Starts {fmtDate(choices.pending && choices.pending.effectiveDate)}
          </button>
          {choices.canSwitch && (
            <button
              className="nv-btn nv-btn-ghost-d"
              style={stacked}
              disabled={saving}
              onClick={this.cancelSwitch}
            >
              Cancel switch
            </button>
          )}
        </div>
      );
    }
    if (!choices.canSwitch || !c) {
      return (
        <button className={`nv-btn ${tone}`} disabled title={choices.reason}>
          Owner can switch
        </button>
      );
    }
    return (
      <button
        className={`nv-btn ${tone}`}
        disabled={saving}
        onClick={() => this.startConfirm(pid)}
      >
        Switch to {plan.name}
      </button>
    );
  };

  renderNotices(choices) {
    const { error } = this.state;
    const box = (color, background, border, children) => (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          flexWrap: 'wrap',
          maxWidth: 720,
          margin: '16px auto 0',
          padding: '10px 16px',
          borderRadius: 14,
          fontSize: 14,
          fontWeight: 600,
          color,
          background,
          border: `1px solid ${border}`,
        }}
      >
        {children}
      </div>
    );
    return (
      <>
        {choices.pending &&
          box(
            C.amber,
            C.amberBg,
            '#fcd34d',
            <>
              <EventIcon style={{ fontSize: 18 }} />
              Switching to <b>{choices.pending.planName}</b>
              {(choices.pending.addOnNames || []).length > 0 ? (
                <>
                  {' '}
                  with <b>{choices.pending.addOnNames.join(', ')}</b>
                </>
              ) : null}{' '}
              on {fmtDate(choices.pending.effectiveDate)}.
            </>
          )}
        {!choices.canSwitch &&
          choices.reason &&
          box(C.muted, '#fff', C.line, `${choices.reason}.`)}
        {error && box(C.red, C.redBg, '#fecaca', error)}
      </>
    );
  }

  render() {
    const choices = this.choices();
    const fromServer = (choices.plans || []).map((p) => p.id);
    const planOrder = fromServer.length
      ? fromServer.filter((pid) => PLANS[pid])
      : PUBLIC_PLAN_ORDER;
    const current = (choices.plans || []).find((p) => p.current);
    return (
      // No header or Back of its own: the page toolbar (UserProfile) reads
      // "Manage Subscription" while this is open and its Back closes it.
      <div style={{ background: '#f5f8ff', minHeight: '100%' }}>
        <div className="nv-landing nv-embedded">
          <PricingPlans
            id="nv-manage-plans"
            planOrder={planOrder}
            // The location is the headline: it says which of the owner's
            // sites this plan change is for.
            eyebrow="Choose your plan"
            title={this.props.companyName || 'Choose your plan'}
            subtitle={`${
              current ? `You're on ${current.name}. ` : ''
            }Pick a plan and its add-ons — the change starts on your next billing date, ${fmtDate(
              choices.switchDate
            )}, and your current features stay until then.`}
            showFreeTrial={false}
            showAddOns={false}
            showCompare={false}
            intro={this.renderNotices(choices)}
            renderAction={this.renderAction}
            renderPrice={this.renderPrice}
            renderPriceNote={this.renderPriceNote}
            renderNameBadge={this.renderNameBadge}
            cardClassName={this.cardClassName}
          />
        </div>
      </div>
    );
  }
}

export default ManageSubscriptionScreen;
